import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  input,
} from '@angular/core';

import { startWith } from 'rxjs';

import { injectTranslate, TranslatePipe } from '@core/i18n-legacy';
import {
  CheckboxComponent,
  DropdownComponent,
  DropdownHeaderComponent,
  DropdownItemComponent,
  IconButtonComponent,
  IconComponent,
  NestedTemplateTriggerDirective,
  RtTooltip,
} from '@core/user-interface-legacy';
import { injectUntilDestroyed } from '@core/utils-legacy';
import type { DataSourceSwimlane, SwimlaneConfig, ViewForm } from '@core/views-types';

import { ViewsAnalytics } from '../../../analytics/views-analytics';

interface SwimlaneWithNone extends Omit<DataSourceSwimlane, 'id'> {
  id: string | null;
}

@Component({
  selector: 'view-settings-swimlanes',
  imports: [
    DropdownHeaderComponent,
    DropdownItemComponent,
    IconButtonComponent,
    IconComponent,
    TranslatePipe,
    NestedTemplateTriggerDirective,
    NgTemplateOutlet,
    CheckboxComponent,
    RtTooltip,
  ],
  templateUrl: './view-settings-swimlanes.component.html',
  styleUrl: './view-settings-swimlanes.component.less',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ViewSettingsSwimlanesComponent {
  public layoutControl = input.required<ViewForm['controls']['layout']>();
  public swimlanes = input.required<DataSourceSwimlane[]>();

  public swimlanesWithNone = computed<SwimlaneWithNone[]>(() => [
    { id: null, name: this.translate('app.views.view_settings.hide_swimlanes') },
    ...this.swimlanes(),
  ]);
  public currentSwimlane: SwimlaneWithNone | null = null;

  public viewsAnalytics = inject(ViewsAnalytics);
  public mainDropdown = inject(DropdownComponent);

  private untilDestroyed = injectUntilDestroyed();
  private cdRef = inject(ChangeDetectorRef);
  private translate = injectTranslate();

  public ngOnInit() {
    this.watchSwimlane();
  }

  public changeSwimlane(swimlane: SwimlaneWithNone) {
    const { swimlane: swimlaneCtrl } = this.layoutControl().controls;
    const swimlaneConfig = swimlaneCtrl.getRawValue();
    const value = swimlane.id
      ? { configId: swimlane.id, hideEmpty: swimlaneConfig?.hideEmpty ?? true }
      : null;
    swimlaneCtrl.markAsDirty();
    swimlaneCtrl.setValue(value);
    this.viewsAnalytics.settingsSwimlaneSelect({ swimlane: swimlane.id });
  }

  public onHideEmptyClick() {
    const { swimlane: swimlaneCtrl } = this.layoutControl().controls;
    const swimlaneConfig = swimlaneCtrl.getRawValue();
    if (!swimlaneConfig) return;

    const hideEmpty = !swimlaneConfig.hideEmpty;
    swimlaneCtrl.markAsDirty();
    swimlaneCtrl.setValue({ ...swimlaneConfig, hideEmpty });
    this.viewsAnalytics.settingsSwimlaneHideEmpty({ hideEmpty });
  }

  private watchSwimlane() {
    const { swimlane } = this.layoutControl().controls;
    swimlane.valueChanges.pipe(startWith(swimlane.value), this.untilDestroyed).subscribe(value => {
      this.currentSwimlane = this.getCurrentSwimlane(value);
      this.cdRef.detectChanges();
    });
  }

  private getCurrentSwimlane(formSwimlane: SwimlaneConfig | null) {
    return (
      this.swimlanesWithNone().find(swimlane => swimlane.id === (formSwimlane?.configId ?? null)) ??
      null
    );
  }
}
